import React, { useState, useEffect, useRef } from 'react';
import { Box, Chip, Stack, alpha, useTheme, keyframes, useMediaQuery } from '@mui/material';

const glowPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 215, 0, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 215, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 215, 0, 0);
  }
`;

const floatAnimation = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-3px);
  }
  100% {
    transform: translateY(0px);
  }
}`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}`;

const CategoryFilterBar = ({ 
  categories, 
  selectedCategory, 
  handleCategoryChange, 
  language,
  variant = '3d',
  enableAnimation = true,
  scrollOnClick = true
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const [hoveredChip, setHoveredChip] = useState(null);
  const [clickedChip, setClickedChip] = useState(null);
  const [appBarHeight, setAppBarHeight] = useState(80);
  const barRef = useRef(null);
  const staticBarRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const theme = useTheme();
  
  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isSmallMobile = useMediaQuery('(max-width: 480px)');

  // Dynamically calculate app bar height
  useEffect(() => {
    const updateAppBarHeight = () => {
      const appBar = document.querySelector('header.MuiPaper-root, .MuiAppBar-root');
      if (appBar) {
        const height = appBar.offsetHeight;
        setAppBarHeight(height);
      } else {
        // Fallback heights for different screen sizes
        if (isSmallMobile) setAppBarHeight(56);
        else if (isMobile) setAppBarHeight(64);
        else if (isTablet) setAppBarHeight(72);
        else setAppBarHeight(80);
      }
    };

    updateAppBarHeight();
    window.addEventListener('resize', updateAppBarHeight);
    return () => window.removeEventListener('resize', updateAppBarHeight);
  }, [isMobile, isTablet, isSmallMobile]);

  // Get responsive top offset
  const getTopOffset = () => {
    if (isSmallMobile) return 56;
    if (isMobile) return 64;
    if (isTablet) return 72;
    return appBarHeight;
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (staticBarRef.current) {
        const staticBarPosition = staticBarRef.current.offsetTop;
        // Adjust threshold based on screen size
        const threshold = isMobile ? appBarHeight - 20 : appBarHeight - 10;
        setIsSticky(currentScrollY > staticBarPosition - threshold);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [appBarHeight, isMobile]);

  const handleChipClick = (categoryId, event) => {
    setClickedChip(categoryId);
    setTimeout(() => setClickedChip(null), 300);
    handleCategoryChange({ target: { value: categoryId } });
    
    if (scrollOnClick && window.scrollY > 300) {
      window.scrollTo({ top: 200, behavior: 'smooth' });
    }
  };

  // Responsive chip styles
  const getResponsiveChipStyles = () => {
    if (isSmallMobile) {
      return {
        fontSize: '0.7rem',
        px: 1.2,
        py: 1.5,
        minWidth: 'auto',
      };
    }
    if (isMobile) {
      return {
        fontSize: '0.75rem',
        px: 1.5,
        py: 2,
        minWidth: 'fit-content',
      };
    }
    if (isTablet) {
      return {
        fontSize: '0.85rem',
        px: 2,
        py: 2.2,
        minWidth: 'fit-content',
      };
    }
    return {
      fontSize: '0.9rem',
      px: 2.2,
      py: 2.5,
      minWidth: 'fit-content',
    };
  };

  const getChipStyles = (isSelected, isHovered) => {
    const responsiveStyles = getResponsiveChipStyles();
    
    const baseStyles = {
      flexShrink: 0,
      minWidth: 'fit-content',
      fontWeight: isSelected ? 800 : 600,
      fontSize: responsiveStyles.fontSize,
      px: responsiveStyles.px,
      py: responsiveStyles.py,
      borderRadius: isSmallMobile ? 8 : 12,
      transition: enableAnimation ? 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
      cursor: 'pointer',
      letterSpacing: isSmallMobile ? '0.01em' : '0.03em',
      position: 'relative',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    };

    // 3D Variant
    if (variant === '3d') {
      return {
        ...baseStyles,
        background: isSelected 
          ? 'linear-gradient(135deg, #FFD700 0%, #FFED4E 30%, #FFD700 70%, #FFB800 100%)'
          : 'linear-gradient(135deg, rgba(30, 30, 40, 0.95) 0%, rgba(20, 20, 25, 0.98) 100%)',
        color: isSelected ? '#000000' : '#FFFFFF',
        border: '1px solid',
        borderColor: isSelected ? 'rgba(255, 215, 0, 0.6)' : 'rgba(255, 255, 255, 0.15)',
        boxShadow: isSelected 
          ? `0 ${isSmallMobile ? '4px' : '8px'} ${isSmallMobile ? '10px' : '20px'} rgba(255, 215, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)`
          : `0 ${isSmallMobile ? '2px' : '4px'} ${isSmallMobile ? '6px' : '12px'} rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)`,
        transform: isHovered && !isSmallMobile ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)',
        '&::before': isSelected ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
          animation: isHovered ? `${shimmer} 1.5s infinite` : 'none',
        } : {},
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: '10%',
          width: '80%',
          height: isSmallMobile ? '1px' : '2px',
          background: isSelected ? '#FFD700' : 'transparent',
          borderRadius: '2px',
          transition: 'all 0.3s ease',
        },
        '&:hover': {
          background: isSelected 
            ? 'linear-gradient(135deg, #FFE55C 0%, #FFD700 50%, #FFC107 100%)'
            : 'linear-gradient(135deg, rgba(40, 40, 50, 0.98) 0%, rgba(30, 30, 40, 0.95) 100%)',
          borderColor: '#FFD700',
          color: isSelected ? '#000000' : '#FFD700',
          boxShadow: `0 ${isSmallMobile ? '6px' : '12px'} ${isSmallMobile ? '14px' : '28px'} rgba(255, 215, 0, 0.25)`,
          transform: isSmallMobile ? 'translateY(-2px)' : 'translateY(-4px) scale(1.02)',
          '&::after': {
            background: '#FFD700',
            width: '100%',
            left: 0,
          },
        },
      };
    }

    // Glass Morphism Variant
    if (variant === 'glass') {
      return {
        ...baseStyles,
        background: isSelected 
          ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.9), rgba(255, 200, 0, 0.95))'
          : 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        color: isSelected ? '#000000' : '#FFFFFF',
        border: '1px solid',
        borderColor: isSelected 
          ? 'rgba(255, 215, 0, 0.8)' 
          : 'rgba(255, 255, 255, 0.2)',
        boxShadow: isSelected 
          ? `0 ${isSmallMobile ? '4px' : '8px'} ${isSmallMobile ? '10px' : '20px'} rgba(255, 215, 0, 0.2)`
          : `0 ${isSmallMobile ? '2px' : '4px'} ${isSmallMobile ? '6px' : '12px'} rgba(0, 0, 0, 0.1)`,
        transform: isHovered && !isSmallMobile ? 'translateY(-3px)' : 'translateY(0)',
        '&:hover': {
          background: isSelected 
            ? 'linear-gradient(135deg, #FFE55C, #FFD700)'
            : 'rgba(255, 255, 255, 0.12)',
          transform: isSmallMobile ? 'translateY(-1px)' : 'translateY(-3px)',
        },
      };
    }

    // Default modern variant
    return {
      ...baseStyles,
      backgroundColor: isSelected ? '#FFD700' : alpha(theme.palette.common.white, 0.05),
      color: isSelected ? '#000000' : '#FFFFFF',
      border: isSelected ? '1.5px solid #FFD700' : '1.5px solid rgba(255, 255, 255, 0.1)',
      '&:hover': {
        backgroundColor: isSelected ? '#FFE55C' : alpha('#FFD700', 0.15),
        borderColor: '#FFD700',
        color: isSelected ? '#000000' : '#FFD700',
        transform: enableAnimation && !isSmallMobile ? 'translateY(-2px)' : 'none',
      },
    };
  };

  const getIconSize = () => {
    if (isSmallMobile) return 24;
    if (isMobile) return 36;
    if (isTablet) return 36;
    return 36;
  };

  const ChipContent = ({ category, isSelected }) => (
    <Stack direction="row" spacing={isSmallMobile ? 0.8 : 1.2} alignItems="center">
      {category.ico && (
        <Box 
          component="img" 
          src={category.ico} 
          alt=""
          sx={{
            width: getIconSize(),
            height: getIconSize(),
            objectFit: 'contain',
            borderRadius: 1.5,
            opacity: isSelected ? 1 : 0.85,
            transition: 'all 0.2s ease',
          }}
        />
      )}
      <Box 
        component="span" 
        sx={{ 
          whiteSpace: 'nowrap',
          fontWeight: isSelected ? 800 : 600,
          fontSize: 'inherit',
        }}
      >
        {language === 'ar' ? category.name : category.name_en || category.name}
      </Box>
      {isSelected && variant === '3d' && !isSmallMobile && (
        <Box
          component="span"
          sx={{
            width: isMobile ? 6 : 8,
            height: isMobile ? 6 : 8,
            borderRadius: '50%',
            backgroundColor: '#000000',
            opacity: 0.7,
            animation: `${glowPulse} 1.5s infinite`,
          }}
        />
      )}
    </Stack>
  );

  const renderCategoryChips = () => (
    <>
      <Chip
        label={
          <Stack direction="row" spacing={isSmallMobile ? 0.8 : 1.2} alignItems="center">
            <Box
              component="span"
              sx={{
                width: getIconSize(),
                height: getIconSize(),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isSmallMobile ? '1rem' : '1.2rem',
              }}
            >
              🍽️
            </Box>
            <span style={{ fontWeight: selectedCategory === '' ? 800 : 600, fontSize: 'inherit' }}>
              {language === 'ar' ? (isSmallMobile ? 'الكل' : 'جميع الأقسام') : (isSmallMobile ? 'All' : 'All Categories')}
            </span>
          </Stack>
        }
        onClick={(e) => handleChipClick('', e)}
        variant={selectedCategory === '' ? 'filled' : 'outlined'}
        sx={getChipStyles(selectedCategory === '', hoveredChip === 'all')}
        onMouseEnter={() => setHoveredChip('all')}
        onMouseLeave={() => setHoveredChip(null)}
        icon={null}
      />
      
      {categories.map((category) => (
        <Chip
          key={category.id}
          label={<ChipContent category={category} isSelected={selectedCategory === category.id} />}
          onClick={(e) => handleChipClick(category.id, e)}
          variant={selectedCategory === category.id ? 'filled' : 'outlined'}
          sx={{
            ...getChipStyles(selectedCategory === category.id, hoveredChip === category.id),
            animation: clickedChip === category.id && !isSmallMobile ? `${floatAnimation} 0.3s ease` : 'none',
          }}
          onMouseEnter={() => setHoveredChip(category.id)}
          onMouseLeave={() => setHoveredChip(null)}
        />
      ))}
    </>
  );

  const scrollContainerStyles = {
    display: 'flex',
    gap: isSmallMobile ? 1 : (isMobile ? 1.5 : 2),
    overflowX: 'auto',
    maxWidth: '100%',
    py: isSmallMobile ? 0.5 : 0.8,
    px: 0.5,
    '&::-webkit-scrollbar': {
      height: isSmallMobile ? 3 : 5,
    },
    '&::-webkit-scrollbar-track': {
      background: alpha(theme.palette.common.white, 0.05),
      borderRadius: 10,
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'linear-gradient(90deg, #FFD700, #FFED4E)',
      borderRadius: 10,
    },
    scrollBehavior: 'smooth',
  };

  return (
    <>
      {/* Static Bar */}
      <Box
        ref={staticBarRef}
        sx={{
          position: 'relative',
          zIndex: 1090,
          background: variant === 'gradient' ? 'transparent' 
            : variant === 'glass' ? 'rgba(13, 13, 13, 0.8)'
            : variant === '3d' ? 'linear-gradient(180deg, #0a0a0f 0%, #0D0D0D 100%)'
            : '#0D0D0D',
          backdropFilter: variant === 'glass' ? 'blur(12px)' : 'none',
          borderBottom: variant !== 'minimal' ? '1px solid rgba(255, 215, 0, 0.15)' : 'none',
          borderTop: variant !== 'minimal' ? '1px solid rgba(255, 215, 0, 0.08)' : 'none',
          py: isSmallMobile ? 1 : (isMobile ? 1.2 : 1.8),
          mb: isSmallMobile ? 2 : (isMobile ? 3 : 4),
          width: '100%',
          ...(variant === '3d' && {
            boxShadow: `0 ${isSmallMobile ? '4px' : '8px'} 20px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)`,
          }),
        }}
      >
        <Box sx={{ maxWidth: 'lg', mx: 'auto', px: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box 
            ref={scrollContainerRef}
            sx={scrollContainerStyles}
          >
            {renderCategoryChips()}
          </Box>
        </Box>
      </Box>

      {/* Sticky Bar */}
      <Box
        ref={barRef}
        sx={{
          position: 'fixed',
          top: getTopOffset(),
          left: 0,
          right: 0,
          zIndex: 1100,
          transform: isSticky ? 'translateY(0)' : 'translateY(-100%)',
          transition: enableAnimation ? 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
          visibility: isSticky ? 'visible' : 'hidden',
          pointerEvents: isSticky ? 'auto' : 'none',
          boxShadow: isSticky ? `0 ${isSmallMobile ? '6px' : '12px'} ${isSmallMobile ? '16px' : '32px'} rgba(0, 0, 0, 0.5)` : 'none',
        }}
      >
        <Box
          sx={{
            background: variant === 'gradient' ? 'rgba(13, 13, 13, 0.95)' 
              : variant === 'glass' ? 'rgba(13, 13, 13, 0.85)'
              : variant === '3d' ? 'linear-gradient(180deg, #0a0a0f 0%, #0D0D0D 100%)'
              : '#0D0D0D',
            backdropFilter: variant === 'glass' ? 'blur(16px)' : 'blur(12px)',
            borderBottom: '2px solid #FFD700',
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            py: isSmallMobile ? 0.8 : (isMobile ? 1 : 1.5),
            ...(variant === '3d' && {
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.05)',
            }),
          }}
        >
          <Box sx={{ maxWidth: 'lg', mx: 'auto', px: { xs: 1.5, sm: 2, md: 3 } }}>
            <Box sx={scrollContainerStyles}>
              {renderCategoryChips()}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default CategoryFilterBar;